import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  HomeIcon, 
  CalendarIcon, 
  ClockIcon, 
  CurrencyDollarIcon, 
  ChartBarIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

/**
 * Componente de navegação para o painel do profissional
 * Fornece navegação lateral em desktop e barra inferior em mobile
 */
const ProfessionalNavigation = ({ salonLogo, professionalInfo, onLogout, salonId }) => {
  const location = useLocation();
  const [isCompactMode, setIsCompactMode] = useState(false);
  
  // Monitorar tamanho da tela para decidir se usamos modo compacto
  useEffect(() => {
    const checkScreenSize = () => {
      // Definir modo compacto para telas entre 768px e 1024px
      setIsCompactMode(window.innerWidth >= 768 && window.innerWidth < 1024);
    };
    
    // Verificar ao carregar
    checkScreenSize();
    
    // Verificar quando a tela é redimensionada
    window.addEventListener('resize', checkScreenSize);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  // Debug logs
  console.log(`[ProfessionalNavigation] Renderizando com salonId: ${salonId || 'não disponível'}`);
  console.log(`[ProfessionalNavigation] Path atual: ${location.pathname}`);
  
  if (!salonId) {
    console.warn('[ProfessionalNavigation] salonId não fornecido');
    return null;
  }
  
  // Verificar se o item está ativo
  const isItemActive = (path) => {
    if (path === '/') {
      // Rota do dashboard é ativa apenas no caminho exato
      return location.pathname === `/professional` || 
             location.pathname === `/professional/`;
    }
    
    // Para outras rotas, verificar se o caminho é exatamente igual ao path,
    // não apenas se contém o path (para evitar ativação de múltiplos itens)
    return location.pathname === `/professional/${path}`;
  };
  
  // Definir itens de navegação com base no salonId
  const navigationItems = [
    {
      path: '/',
      label: 'Dashboard',
      icon: HomeIcon,
      mobileVisible: true
    },
    {
      path: 'agenda',
      label: 'Minha Agenda',
      icon: CalendarIcon,
      mobileVisible: true
    },
    {
      path: 'agendamentos',
      label: 'Agendamentos',
      icon: ClockIcon,
      mobileVisible: true
    },
    {
      path: 'comissoes',
      label: 'Comissões',
      icon: CurrencyDollarIcon,
      mobileVisible: true
    },
    {
      path: 'desempenho',
      label: 'Desempenho',
      icon: ChartBarIcon,
      mobileVisible: true
    },
    {
      path: 'perfil',
      label: 'Perfil',
      icon: UserIcon,
      mobileVisible: false
    }
  ];

  // Filtrar itens visíveis no mobile
  const mobileItems = navigationItems.filter(item => item.mobileVisible);
  
  // Renderiza um item de navegação desktop com texto (estilo Bookly)
  const renderDesktopNavItem = (item) => {
    const isActive = isItemActive(item.path);
    
    // No modo compacto, mostramos apenas o ícone
    if (isCompactMode) {
      return (
        <Link 
          to={item.path === '/' ? `/professional` : `/professional/${item.path}`}
          className={`flex items-center justify-center w-12 h-12 rounded-full ${
            isActive ? 'bg-blue-50' : 'hover:bg-gray-100'
          }`}
          title={item.label}
        >
          <item.icon className={`w-6 h-6 ${isActive ? 'text-[#3b83f6]' : 'text-gray-500'}`} />
        </Link>
      );
    }
    
    // Modo padrão com texto
    return (
      <Link 
        to={item.path === '/' ? `/professional` : `/professional/${item.path}`}
        className={`flex items-center w-full px-3 py-2.5 rounded-xl transition-colors ${
          isActive ? 'bg-blue-50' : 'hover:bg-gray-100'
        }`}
      >
        <item.icon className={`w-5 h-5 mr-2.5 ${isActive ? 'text-[#3b83f6]' : 'text-gray-600'}`} />
        <span className={`text-sm ${isActive ? 'text-[#3b83f6] font-medium' : 'text-gray-700'}`}>
          {item.label}
        </span>
      </Link>
    );
  };

  return (
    <>
      {/* Barra de navegação lateral (desktop) - Estilo como AdminNavigation */}
      <div className={`hidden md:flex flex-col border-r border-gray-200 h-screen fixed top-0 left-0 overflow-y-auto bg-white z-50 ${
        isCompactMode ? 'w-20 items-center' : 'w-56'
      }`}>
        <div className={`flex flex-col h-full ${isCompactMode ? 'p-2 items-center' : 'p-4'}`}>
          {/* Logo/Ícone do salão */}
          <div className={`flex justify-center ${isCompactMode ? 'mb-6 mt-2' : 'mb-10 mt-2'}`}>
            <img 
              src={salonLogo || "/images/logo.svg"} 
              alt="Logo" 
              className={`${isCompactMode ? 'h-8' : 'h-9'} object-contain`} 
            />
          </div>
          
          {/* Nome do profissional */}
          {!isCompactMode && (
            <div className="text-center mb-6">
              <h2 className="text-sm font-medium text-gray-700">
                {professionalInfo?.name || 'Profissional'}
              </h2>
            </div>
          )}
          
          {/* Itens de navegação com texto ou apenas ícones dependendo do modo */}
          <div className={`${isCompactMode ? 'flex flex-col items-center space-y-4' : 'space-y-1.5'}`}>
            {navigationItems.map(item => (
              <div key={item.path}>
                {renderDesktopNavItem(item)}
              </div>
            ))}
          </div>
          
          {/* Botão de logout */}
          {onLogout && (
            <button 
              onClick={onLogout}
              className={isCompactMode 
                ? "mt-auto flex items-center justify-center w-12 h-12 rounded-full hover:bg-gray-100"
                : "flex items-center px-3 py-2.5 rounded-xl hover:bg-gray-100 mt-auto"
              }
              title="Sair"
            >
              {isCompactMode ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2.5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span className="text-sm text-gray-700">Sair</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
      
      {/* Barra de navegação inferior (mobile) */}
      <div className="fixed bottom-0 left-0 right-0 pb-safe bg-white border-t border-gray-200 z-10 md:hidden">
        <div className="flex justify-around items-center h-14">
          {mobileItems.map(item => {
            const isActive = isItemActive(item.path);
            return (
              <div key={item.path}>
                <Link 
                  to={item.path === '/' ? `/professional` : `/professional/${item.path}`}
                  className={`p-2 relative ${isActive ? 'text-blue-500' : 'text-gray-500'}`}
                >
                  <item.icon className="w-6 h-6" />
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default ProfessionalNavigation; 